import type {
  BulkUpdateStatusBody,
  BulkUpdateStatusResponse,
} from "@nlc/shared-types";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";
import {
  invalidateStatusChangeQueries,
  scheduleQueueAutomationRefetch,
} from "@/features/requests/utils/requestInvalidation";
import apiClient from "@/lib/api/client";

interface UseBulkChangeStatusOptions {
  onError?: (error: Error) => void;
  onSuccess?: (data: BulkUpdateStatusResponse) => void;
}

export function useBulkChangeStatus(options: UseBulkChangeStatusOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: BulkUpdateStatusBody) =>
      await apiClient
        .url("/requests/list/bulk-update-status")
        .post(body)
        .json<BulkUpdateStatusResponse>(),
    onError: (error: Error) => {
      toast.add({
        description: "Failed to update request status. Please try again.",
        timeout: 3000,
        title: "Update Failed",
        type: "error",
      });
      options.onError?.(error);
    },
    onSuccess: (data) => {
      invalidateStatusChangeQueries(queryClient);
      scheduleQueueAutomationRefetch(queryClient);

      if (data.failed > 0) {
        toast.add({
          description: `${data.updated} request${data.updated === 1 ? "" : "s"} updated, ${data.failed} failed.`,
          timeout: 4000,
          title: "Partially Completed",
          type: "warning",
        });
      } else {
        toast.add({
          description: `${data.updated} request${data.updated === 1 ? "" : "s"} updated successfully.`,
          timeout: 3000,
          title: "Status Updated",
          type: "success",
        });
      }

      options.onSuccess?.(data);
    },
  });
}
